<template>
  <el-card
    :class="{'marker--sample-project-card': $permissions.isSampleInternalProjectID(entity.id)}"
    class="dashboard-card-entity"
    shadow="hover"
  >
    <!-- Rename / Archive -->
    <more-button-dropdown
      v-if="$permissions.isAdminOrOwner"
      :force-visibility="showMoreOptions"
      class="dashboard-card-entity__more-dropdown"
      popper-min-width-size="small"
      placement="bottom-end"
    >
      <template #options-list>
        <ul>
          <li @click="enableRenameInput">
            <i class="ddi ddi-edit" />
            <span>Rename</span>
          </li>
          <li @click="archive">
            <i class="ddi ddi-lock" />
            <span>Archive</span>
          </li>
        </ul>
      </template>
    </more-button-dropdown>
    <!-- Scheduled Tasks -->
    <router-link v-if="$permissions.isNotGuest && !$permissions.isAppSumoAccount && entityType === 'projects'" :to="getScheduleRoute()" class="dashboard-card-entity__schedule" @click.prevent.stop>
      <i class="ddi ddi-clock" />
    </router-link>
    <!-- Favorite -->
    <favorite-button 
      :is-favorite="entity.isFavorite"
      class="dashboard-card-entity__favorite"
      @click="$emit('favorite-clicked')"
    />
    <!-- Complete setup button -->
    <div v-if="showSetupButton" class="dashboard-card-entity__complete-setup">
      <dd-button
        full-width
        color="orange"
        size="medium"
        @click="goToNextSetupStep"
      >{{finishSetupText}}</dd-button>
    </div>
    <!-- Members -->
    <div class="dashboard-card-entity__members">
      <avatar-list :max-num-avatars="5" :people="members" @click="goToHome" />
      <div>
        <quick-add-member-picker
          v-if="$permissions.isAdminOrOwner && !$permissions.isSampleInternalProjectID(entity.id)"
          :entity-id="entity.id"
          :entity-type="entityType"
          placement="bottom-end"
          class="add-members-button"
          stumpy
          @members-added="onMembersAdded"
        />
      </div>
    </div>
    <!-- Sample project ribbon -->
    <sample-ribbon
      v-if="$permissions.isSampleInternalProjectID(entity.id)"
      size="medium"
      @click="goToHome"
    />
    <div class="dashboard-card-entity__main" @click="goToHome">
      <!-- Header -->
      <div class="dashboard-card-entity__header">
        <el-input
          v-if="showRenameInput"
          ref="renameRef"
          v-model="updatedName"
          class="rename-input"
          maxlength="100"
          @blur="updateName"
          @click.prevent.stop
          @keydown.enter="updateName"
        />
        <span v-else class="entity-name">{{ entity.name }}</span>
        <span class="entity-last-update">{{ entity.lastUpdate }}</span>
      </div>
      <div v-if="!showSetupButton" class="dashboard-card-entity__content">
        <!-- LINE CHART -->
        <el-tooltip :show-arrow="true" placement="bottom" content="Activity in past two weeks">
          <div class="line-chart">
            <ul v-if="hasDailyActivityLog" >
              <li v-for="(value, index) in dailyActivityLog.listDailyCounts" :key="index" :style="getPlotPosition(index)">
                <div 
                  :style="getLengthAndAngle(index)" 
                  :class="'line-segment--' + entityType"
                  class="line-segment" />
              </li>
            </ul>
            <loading-placeholder v-else :lines="2" />
          </div>
        </el-tooltip>
        <!-- COUNTS -->
        <div class="summary">
          <template v-if="entity.activeCount">
            <router-link :to="getActiveRoute()" @click.prevent.stop>
              <div :class="'summary__total--' + entityType" class="summary__total">
                {{ limitDigits(entity.activeCount) }} {{ activeCountLabel }}
              </div>
            </router-link>
            <router-link :to="getAssignedToYouRoute()" @click.prevent.stop>
              <div class="summary__assigned">
                {{ limitDigits(entity.assignedCount) }} assigned to you
                <avatar :person="profile" :size="14" />
              </div>
            </router-link>
          </template>
          <router-link v-else :to="getActiveRoute()" @click.prevent.stop>
            <div class="summary__assigned">
              No {{ activeCountLabel }}
            </div>
          </router-link>
        </div>
      </div>
    </div>
  </el-card>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'

/* Mixins */
import EntityTypeMixin from '@/mixins/entity-type.mixin'

/* Components */
import QuickAddMemberPicker from '@/components/entity/quick-add-member-picker.vue'

/* Helpers */
import RouteAssistant from '@/helpers/route-assistant'

/* Data types */
import { DefaultFilterIDs, EEntities } from '@/types'
import {
  IGenericJSONMessage,
  IPersonBase,
  IProjectDailyActivityLogsDailyActivityLog,
  IProjectsOutputProjectForHome,
} from '@/api/types/response.types'

/* API */
import MailboxesAPI from '@/api/modules/mailboxes.api'
import InternalProjectsAPI from '@/api/modules/internal-projects.api'

export default defineComponent({
  name: 'DashboardCardEntity',

  components: {
    QuickAddMemberPicker,
  },

  extends: EntityTypeMixin,

  props: {
    entity: {
      type: Object as () => IProjectsOutputProjectForHome,
      required: true,
    },
    profile: {
      type: Object as () => IPersonBase,
      required: true,
    },
    dailyActivityLog: {
      type: Object as () => IProjectDailyActivityLogsDailyActivityLog,
      default: null,
    },
  },

  emits: ['entity-archived', 'favorite-clicked'],

  data() {
    return {
      RouteAssistant,
      isSubmitting: false,
      showMoreOptions: false,
      showRenameInput: false,
      updatedName: '',
      members: [] as IPersonBase[],
    }
  },

  computed: {
    hasDailyActivityLog(): boolean {
      return this.dailyActivityLog !== null
    },
    activeCountLabel(): string {
      if (this.entityType === EEntities.PROJECTS) {
        return this.entity.activeCount === 1 ? 'active task' : 'active tasks'
      } else if (this.entityType === EEntities.MAILBOXES) {
        return this.entity.activeCount === 1
          ? 'active conversation'
          : 'active conversations'
      }
      return ''
    },
    setupCompleted(): boolean {
      return this.entity.setupStatus === 'complete'
    },
    showSetupButton(): boolean {
      return (
        !this.setupCompleted &&
        !this.$screen.isMobile(this.$mq) &&
        this.$permissions.isAdminOrOwner
      )
    },
    finishSetupText(): string {
      if (this.setupCompleted) {
        return ''
      }
      if (this.entity.setupStatus === 'forwarding incomplete') {
        return 'Finish Auto-Forwarding Setup'
      } else if (this.entity.setupStatus === 'from incomplete') {
        return 'Finish Outgoing Email Setup'
      } else {
        return 'Set up this Mailbox'
      }
    },
  },

  watch: {
    entity: {
      immediate: true,
      handler(value) {
        this.members = value.listMembers
      },
    },
  },

  methods: {
    async updateName() {
      if (
        this.updatedName.trim() === '' ||
        this.updatedName.trim() === this.entity.name
      ) {
        this.showRenameInput = false
        return
      }

      // Checked outside the try: its finally would clear the flag that a save still in flight set
      if (this.isSubmitting) {
        return
      }

      try {
        this.isSubmitting = true

        let response: IGenericJSONMessage = null

        if (this.entityType === EEntities.PROJECTS) {
          response = await InternalProjectsAPI.put_Title(
            this.entity.id,
            this.updatedName,
          )
        } else if (this.entityType === EEntities.MAILBOXES) {
          response = await MailboxesAPI.put_Title(
            this.entity.id,
            this.updatedName,
          )
        }

        this.entity.name = this.updatedName
        this.showRenameInput = false
        this.$messaging.showSuccess(response.message)
        this.updatedName = ''
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        this.isSubmitting = false
      }
    },
    async archive() {
      try {
        this.closeOptions()
        let response: IGenericJSONMessage = null
        if (this.entityType === EEntities.PROJECTS) {
          response = await InternalProjectsAPI.post_Archive(this.entity.id)
        } else if (this.entityType === EEntities.MAILBOXES) {
          response = await MailboxesAPI.post_Archive(this.entity.id)
        }
        this.$messaging.showSuccess(response.message)
        this.$emit('entity-archived', this.entity)
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        this.isSubmitting = false
      }
    },
    async onMembersAdded() {
      if (this.entityType === EEntities.PROJECTS) {
        this.members = await InternalProjectsAPI.get_Members(this.entity.id)
      } else if (this.entityType === EEntities.MAILBOXES) {
        this.members = await MailboxesAPI.get_Members(this.entity.id)
      }
    },
    enableRenameInput() {
      this.closeOptions()
      this.$nextTick(() => {
        // @ts-ignore
        this.$refs.renameRef.focus()
      })
      this.showRenameInput = true
      this.updatedName = ''
    },
    disableRenameInput() {
      this.showRenameInput = false
    },
    closeOptions() {
      this.showMoreOptions = true
      this.$nextTick(() => {
        this.showMoreOptions = false
      })
    },
    limitDigits(value: number) {
      return value < 1000 ? value : '999+'
    },
    getPlotPosition(index: number): string {
      return (
        '--y: ' +
        this.getYPosition(index) +
        'px; --x: ' +
        this.getXPosition(index) +
        'px;'
      )
    },
    getYPosition(index: number): number {
      const bottomPad = 10
      const maxHeight = 40 - bottomPad
      const curValue = this.dailyActivityLog.listDailyCounts[index]
      const maxValue = Math.max.apply(
        Math,
        this.dailyActivityLog.listDailyCounts,
      )
      return curValue === 0
        ? bottomPad
        : bottomPad + (curValue / maxValue) * maxHeight
    },
    getXPosition(index: number): number {
      const leftPad = 0
      return leftPad + index * 6
    },
    getLengthAndAngle(index: number): string {
      if (index === this.dailyActivityLog.listDailyCounts.length - 1) {
        return ''
      }

      const y = this.getYPosition(index + 1) - this.getYPosition(index)
      const x = this.getXPosition(index + 1) - this.getXPosition(index)
      const opp = this.getYPosition(index) - this.getYPosition(index + 1)
      const hyp = Math.sqrt(Math.pow(y, 2) + Math.pow(x, 2))
      const angle = Math.asin(opp / hyp) * (180 / Math.PI)

      return '--length: ' + hyp + '; --angle: ' + angle
    },
    handleFavoriteClick() {
      this.$emit('favorite-clicked')
    },
    goToNextSetupStep() {
      if (this.entity.setupStatus === 'from incomplete') {
        RouteAssistant.goToMailboxSettingsOutgoingEmailRoute(
          this.$router,
          this.$route.params.accountId,
          this.entity.id,
        )
      } else if (this.entity.setupStatus === 'forwarding incomplete') {
        RouteAssistant.goToMailboxSettingsAutoForwardingRoute(
          this.$router,
          this.$route.params.accountId,
          this.entity.id,
        )
      } else {
        RouteAssistant.goToMailboxSettingsDirectConnectionRoute(
          this.$router,
          this.$route.params.accountId,
          this.entity.id,
        )
      }
    },
    goToHome() {
      switch (this.entityType) {
        case EEntities.MAILBOXES:
          return RouteAssistant.goToMailbox(
            this.$router,
            this.$route.params.accountId,
            this.entity.id,
            this.entity.name,
          )
        case EEntities.PROJECTS:
          return RouteAssistant.goToProjectLanding(
            this.$router,
            this.$route.params.accountId,
            this.entity.id,
            this.$permissions,
            this.entity.name,
          )
      }
    },
    getScheduleRoute() {
      return RouteAssistant.getProjectScheduleRoute(
        this.$route.params.accountId,
        this.entity.id,
      )
    },
    getActiveRoute() {
      switch (this.entityType) {
        case EEntities.MAILBOXES:
          return RouteAssistant.getMailboxLandingRoute(
            this.$route.params.accountId,
            this.entity.id,
            this.entity.name,
            DefaultFilterIDs.ACTIVE,
          )
        case EEntities.PROJECTS:
          return RouteAssistant.getProjectLandingRoute(
            this.$route.params.accountId,
            this.entity.id,
            this.$permissions,
            this.entity.name,
            DefaultFilterIDs.ACTIVE,
          )
      }
    },
    getAssignedToYouRoute() {
      switch (this.entityType) {
        case EEntities.MAILBOXES:
          return RouteAssistant.getMailboxLandingRoute(
            this.$route.params.accountId,
            this.entity.id,
            this.entity.name,
            DefaultFilterIDs.ASSIGNED_TO_YOU,
          )
        case EEntities.PROJECTS:
          return RouteAssistant.getProjectLandingRoute(
            this.$route.params.accountId,
            this.entity.id,
            this.$permissions,
            this.entity.name,
            DefaultFilterIDs.ASSIGNED_TO_YOU,
          )
      }
    },
  },
})
</script>
<style lang="scss">
.dashboard-card-entity {
  position: relative;
  overflow: visible;
  width: 290px;
  height: 172px;
  color: var(--color-text-secondary);

  .loading-placeholder {
    padding-top: 4px;
  }

  .sample-ribbon {
    bottom: 42px;
  }

  &:hover {
    cursor: pointer;
    .line-chart {
      opacity: 1;
    }
  }

  .line-chart {
    opacity: 0.9;
    height: 50px;
    position: relative;
    width: 146px;
    margin: 0 10px 0 0;

    > ul {
      list-style: none;
      margin: 0;
      padding: 0;
    }

    .line-segment {
      bottom: var(--y);
      height: 1px;
      left: var(--x);
      position: absolute;
      width: calc(var(--length) * 1px);
      transform: rotate(calc(var(--angle) * 1deg));
      transform-origin: left bottom;

      &--mailboxes {
        background-color: $mailboxes--theme-color;
      }

      &--projects {
        background-color: $projects--theme-color;
      }
    }
  }

  &__header {
    display: flex;
    flex-direction: column;
    max-width: 100%;

    .rename-input {
      max-width: calc(100% - 48px);
      top: -1px;
      padding-left: 1px;
      margin: 0 !important;
      .el-input__wrapper,
      .el-input__wrapper.is-focus {
        padding: 0;
        border-radius: 0;
        box-shadow: none !important;
      }
      .el-input__inner {
        font-weight: $bold;
        height: auto;
      }
    }

    .entity-name,
    .entity-last-update {
      @include single-line-ellipsis;
      display: block;
      max-width: calc(100% - 48px);
    }

    .entity-name {
      @include lettering--p;
      font-weight: $bold;
    }

    .entity-last-update {
      @include lettering--p-sm;
      color: var(--color-text-third);
    }
  }

  &__content {
    display: flex;
    padding: 8px 0;

    .summary {
      width: 100%;
      @include lettering--p-sm;
      @include single-line-ellipsis;

      &__total {
        &--mailboxes {
          color: $mailboxes--theme-color;
          font-weight: $bold;

          &:hover {
            text-decoration: underline;
          }
        }

        &--projects {
          color: $projects--theme-color;
          font-weight: $bold;
          &:hover {
            text-decoration: underline;
          }
        }
      }

      &__assigned {
        border: $border--solid-light;
        background-color: var(--color-background-primary);
        color: var(--color-text-secondary);
        padding: 1px 8px;
        border-radius: 4px;
        margin-top: 5px;
      }
    }
  }

  &__members {
    position: absolute;
    width: calc(100% - 32px);
    bottom: 14px;
    right: 15px;
    display: flex;
    justify-content: flex-end;
    align-items: center;

    .avatar-list {
      width: 100%;
      text-align: right;
    }

    .add-members-button {
      justify-content: center;
      margin-left: 8px;
    }
  }

  &__complete-setup {
    position: absolute;
    width: calc(100% - 32px);
    top: 84px;
  }

  &__more-dropdown {
    position: absolute;
    top: 16px;
    right: 53px;
  }

  &__schedule {
    position: absolute;
    top: 13px;
    right: 38px;
    color: var(--color-text-third);
  }

  &__favorite.ddi {
    position: absolute;
    top: 16px;
    right: 17px;
  }
}
</style>
